import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ExternalLink } from 'lucide-react';
import { navGroups, navSingles, isGroupActive } from '@/components/navigation';

function MenuItem({ item, onSelect }) {
  const inner = (
    <>
      <span className="mt-0.5 w-9 h-9 shrink-0 rounded-lg bg-secondary/10 flex items-center justify-center group-hover/item:bg-secondary/20 transition-colors">
        <item.icon className="w-4 h-4 text-secondary" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground">{item.label}</span>
          {item.tag && <span className="px-1.5 py-0.5 bg-secondary text-secondary-foreground text-[10px] font-bold rounded">{item.tag}</span>}
          {item.href && <ExternalLink className="w-3 h-3 text-muted-foreground" />}
        </span>
        <span className="block text-xs text-muted-foreground mt-0.5">{item.desc}</span>
      </span>
    </>
  );
  const cls = 'group/item flex items-start gap-3 rounded-xl px-3 py-2.5 hover:bg-muted transition-colors';
  const testId = `nav-item-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  if (item.href) {
    return <a href={item.href} target="_blank" rel="noopener noreferrer" className={cls} onClick={onSelect} data-testid={testId}>{inner}</a>;
  }
  return <Link to={item.to} className={cls} onClick={onSelect} data-testid={testId}>{inner}</Link>;
}

export default function DesktopNav() {
  const location = useLocation();
  const [open, setOpen] = useState(null);
  const closeTimer = useRef(null);

  useEffect(() => { setOpen(null); }, [location.pathname]);

  const show = (id) => { clearTimeout(closeTimer.current); setOpen(id); };
  const hide = () => { closeTimer.current = setTimeout(() => setOpen(null), 120); };

  return (
    <div className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
      {navGroups.map((group) => {
        const active = isGroupActive(group, location.pathname);
        const isOpen = open === group.id;
        return (
          <div key={group.id} className="relative" onMouseEnter={() => show(group.id)} onMouseLeave={hide}>
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : group.id)}
              aria-expanded={isOpen}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold transition-colors ${
                active || isOpen ? 'bg-secondary/15 text-secondary' : 'text-foreground/85 hover:bg-muted hover:text-foreground'
              }`}
              data-testid={`nav-group-${group.id}`}
            >
              <group.icon className="w-4 h-4" />
              {group.label}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50"
                data-testid={`nav-menu-${group.id}`}
              >
                <div className="w-[340px] rounded-2xl bg-card/95 backdrop-blur-xl border border-border shadow-[0_24px_60px_-12px_rgba(0,0,0,0.8)] p-2 animate-in fade-in slide-in-from-top-1 duration-150">
                  <p className="px-3 pt-2 pb-2 text-xs text-muted-foreground border-b border-border/60 mb-1">{group.blurb}</p>
                  {group.items.map((item) => (
                    <MenuItem key={item.label} item={item} onSelect={() => setOpen(null)} />
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {navSingles.map((link) => (
        <Link
          key={link.to}
          to={link.to}
          className={`px-3.5 py-2 rounded-full text-sm font-semibold transition-colors ${
            location.pathname === link.to ? 'bg-secondary/15 text-secondary' : 'text-foreground/85 hover:bg-muted hover:text-foreground'
          }`}
          data-testid={`nav-${link.label.toLowerCase()}`}
        >
          {link.label}
        </Link>
      ))}
    </div>
  );
}
